import { useState } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import SnoozeAttentionSheet from '@/components/FollowUp/SnoozeAttentionSheet';
import { parseTimeFrame } from '@/utils/dater';
import {
    getAttentionReason,
    getAttentionReasonSnooze,
    formatSnoozeUntil,
} from '@/utils/constants/followUp';

/**
 * AttentionReasonsCard — the person's active attention reasons on the care
 * surface (spec §6.1/§11.5). Each reason is its own row with audience scope,
 * first-detected date and snooze state. Managers get a per-reason Snooze
 * action — scoped to that reason (and audience) only, so an attendance
 * snooze never erases contact flags or open work (§11.6).
 */
const AttentionReasonsCard = ({ organizationID, personID, reasons = [], canManage }) => {
    const [snoozing, setSnoozing] = useState(null);

    if (!reasons?.length) return null;

    return (
        <View style={styles.card} testID="follow-up-attention-reasons">
            <Text style={styles.title}>Attention</Text>
            {reasons.map((entry, index) => {
                const reason = typeof entry === 'string' ? { reason: entry } : entry;
                if (!reason?.reason) return null;
                const meta = getAttentionReason(reason.reason);
                const snooze = getAttentionReasonSnooze(reason);

                return (
                    <View
                        key={`${reason.reason}-${reason.audienceID ?? 'org'}-${index}`}
                        testID={`attention-reason-row-${reason.reason}`}
                        style={[styles.reasonRow, !!snooze && styles.reasonRowSnoozed]}
                    >
                        <View style={[styles.iconWrap, !!snooze && styles.iconWrapSnoozed]}>
                            <Unicon
                                name={snooze ? 'moon-outline' : meta.icon}
                                size={16}
                                colorVariant={snooze ? 'textSecondary' : 'warning'}
                            />
                        </View>
                        <View style={styles.reasonBody}>
                            <Text style={[styles.reasonLabel, !!snooze && styles.snoozedText]}>
                                {meta.label}
                            </Text>
                            <Text style={styles.reasonMeta}>
                                {[
                                    reason.audienceName,
                                    formatSnoozeUntil(snooze?.until)
                                        ? `Snoozed until ${formatSnoozeUntil(snooze.until)}`
                                        : reason.firstDetectedAt
                                            ? `Since ${parseTimeFrame(reason.firstDetectedAt)}`
                                            : null,
                                ].filter(Boolean).join(' · ')}
                            </Text>
                            {!!meta.description && !snooze && (
                                <Text style={styles.reasonHint}>{meta.description}</Text>
                            )}
                        </View>
                        {canManage && !snooze && meta.snoozable !== false && (
                            <SolidButton
                                testID={`snooze-${reason.reason}`}
                                size="sm"
                                variant="outlined"
                                type="textSecondary"
                                text="Snooze"
                                onPress={() => setSnoozing(reason)}
                            />
                        )}
                        {!!snooze && (
                            <View style={styles.snoozedBadge}>
                                <Text style={styles.snoozedBadgeText}>Snoozed</Text>
                            </View>
                        )}
                    </View>
                );
            })}

            <SnoozeAttentionSheet
                isOpen={!!snoozing}
                onClose={() => setSnoozing(null)}
                organizationID={organizationID}
                personID={personID}
                reason={snoozing}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
        gap: 10,
        boxShadow: shadows.card,
    },
    title: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    reasonRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    reasonRowSnoozed: { opacity: 0.6 },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.warningContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    iconWrapSnoozed: { backgroundColor: colors.surfaceVariant },
    reasonBody: { flex: 1, minWidth: 0 },
    reasonLabel: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    snoozedText: { color: colors.textSecondary, fontStyle: 'italic' },
    reasonMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
    reasonHint: { fontSize: 12, color: colors.textTertiary, marginTop: 2 },
    snoozedBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 10,
        backgroundColor: colors.surfaceVariant,
    },
    snoozedBadgeText: { fontSize: 11, color: colors.textSecondary, fontStyle: 'italic' },
}));

export default AttentionReasonsCard;
